/**
 * SunPathOverlay — arc + sun icon for the Product Insolation mode.
 *
 * Layout: full-area overlay, centred over the floor plan. The arc is a true
 * circle inscribed in the smaller of the container's two dimensions (via
 * SVG `preserveAspectRatio="xMidYMid meet"` on a square viewBox), so the
 * sun's position visually corresponds to the direction light is hitting the
 * windows.
 *
 * Geometry (base orientation, azimuth = 90° = north up):
 *   - 06:00 anchored at the right (east, cartesian θ = 0°)
 *   - 12:00 at the bottom (south, where the sun is in the northern hemisphere)
 *   - 18:00 at the left (west, cartesian θ = 180°)
 *
 * The whole arc is rotated by the apartment azimuth so cardinal directions
 * match the plan orientation.
 *
 * Read-only V1: drag-the-sun is intentionally deferred to a later iteration.
 */

import { useRef } from 'react'

import { ICON_SIZE, PADDING, VIEW } from '../../constants/overlayDefaults.js'

import styles from './SunPathOverlay.module.scss'

// Square viewBox so the arc is a true circle segment (not an ellipse).
// The SVG inscribes itself in the smaller container dimension via
// preserveAspectRatio="xMidYMid meet", so it's always centred and never
// stretched no matter the plan area's aspect ratio.

const CX = VIEW / 2
const CY = VIEW / 2
const R = VIEW / 2 - PADDING * 2
const HIT_R = 16  // drag hit-area radius in SVG viewBox units

/**
 * Pure helper: given a point in SVG viewBox space (before the arc group's rotation),
 * returns insolationAngleDeg [90, 270] clamped to the arc endpoints.
 * Exported for unit tests.
 */
export function _angleFromSvgPoint(svgX, svgY, svgRotationDeg) {
  const rad = (-svgRotationDeg * Math.PI) / 180
  const dx0 = svgX - CX
  const dy0 = svgY - CY
  const dx = dx0 * Math.cos(rad) - dy0 * Math.sin(rad)
  const dy = dx0 * Math.sin(rad) + dy0 * Math.cos(rad)
  let theta = (Math.atan2(dy, dx) * 180) / Math.PI
  // Snap out-of-arc drags (above horizon, theta < 0) to nearest endpoint
  if (theta < 0) theta = theta >= -90 ? 0 : 180
  // insolationAngleDeg = theta + 90 ∈ [90°, 270°]
  return theta + 90
}

function _polar(thetaDeg) {
  const t = (thetaDeg * Math.PI) / 180
  // Northern-hemisphere sun arcs through the SOUTH side (when North is up).
  // In SVG Y-down that means θ=90° (noon) sits BELOW the horizon line.
  return { x: CX + R * Math.cos(t), y: CY + R * Math.sin(t) }
}

function _arcPath(thetaStartDeg, thetaEndDeg) {
  if (thetaStartDeg === thetaEndDeg) return ''
  const a = _polar(thetaStartDeg)
  const b = _polar(thetaEndDeg)
  const large = Math.abs(thetaEndDeg - thetaStartDeg) > 180 ? 1 : 0
  // Dome on the SOUTH (visually-bottom) side of the horizon line: sweep=1
  // (CW on screen) matches _polar that places θ=90° below CY.
  return `M${a.x},${a.y} A${R},${R} 0 ${large} 1 ${b.x},${b.y}`
}

function _hourToTheta(hour) {
  return (hour - 6) * 15
}

function _angleToTheta(insolationAngleDeg) {
  // slider 90 (06:00) → θ=0 (east); slider 270 (18:00) → θ=180 (west)
  return insolationAngleDeg - 90
}

/**
 * clipType: 'rise' — reveal from top (sunrise), 'set' — hide from bottom (sunset), null — fully visible.
 * clipT: 0..1 progress within the shoulder zone.
 *
 * The clipPath is defined in the icon's local coordinate system (0..size) via a <defs> placed
 * inside the translate group. SVG userSpaceOnUse resolves coordinates in the caller's space,
 * which is exactly the translate group's local space — so no absolute-coords arithmetic needed.
 */
function SunIcon({ x, y, size = 22, clipType = null, clipT = 1, counterRotateDeg = 0 }) {
  let clipRect = null
  if (clipType === 'rise') {
    // Reveal from top: t=0 → nothing visible, t=1 → fully visible.
    // Top of icon = above-horizon part that crests first.
    clipRect = { x: -2, y: -2, width: size + 4, height: size * clipT + 2 }
  } else if (clipType === 'set') {
    // Hide from bottom: t=0 → fully visible, t=1 → nothing visible.
    // Bottom of icon = below-horizon part that sinks first.
    clipRect = { x: -2, y: -2, width: size + 4, height: size * (1 - clipT) + 2 }
  }

  return (
    <g transform={`translate(${x - size / 2}, ${y - size / 2})`}>
      {clipRect && (
        <defs>
          <clipPath id="sun-path-horizon-clip">
            <rect x={clipRect.x} y={clipRect.y} width={clipRect.width} height={clipRect.height} />
          </clipPath>
        </defs>
      )}
      <g transform={counterRotateDeg ? `rotate(${counterRotateDeg}, ${size / 2}, ${size / 2})` : undefined}>
        <g clipPath={clipRect ? 'url(#sun-path-horizon-clip)' : undefined}>
          <circle cx={size / 2} cy={size / 2} r={size * 0.32} fill="#FFCD33" stroke="#fff" strokeWidth={size * 0.12} />

          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i * Math.PI) / 4
            const r1 = size * 0.40
            const r2 = size * 0.52
            return (
              <g key={i}>
                <line
                  x1={size / 2 + r1 * Math.cos(a)}
                  y1={size / 2 + r1 * Math.sin(a)}
                  x2={size / 2 + r2 * Math.cos(a)}
                  y2={size / 2 + r2 * Math.sin(a)}
                  stroke="#fff"
                  strokeWidth={size * 0.2}
                  strokeLinecap="round"
                />
                <line
                  x1={size / 2 + r1 * Math.cos(a)}
                  y1={size / 2 + r1 * Math.sin(a)}
                  x2={size / 2 + r2 * Math.cos(a)}
                  y2={size / 2 + r2 * Math.sin(a)}
                  stroke="#FFCD33"
                  strokeWidth={size * 0.12}
                  strokeLinecap="round"
                />
              </g>
            )
          })}
        </g>
        {clipRect && (
          <line
            x1={-2} y1={clipRect.y + clipRect.height}
            x2={size + 2} y2={clipRect.y + clipRect.height}
            stroke="#94a3b8" strokeWidth={size * 0.07} strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        )}
      </g>
    </g>
  )
}

/**
 * Computes the array of [hStart, hEnd] segments inside [7,17] hours that are
 * NOT covered by the supplied sun-light ranges (drawn grey).
 */
function _uncoveredSegments(sunLightRanges) {
  const out = []
  let cursor = 7
  for (const r of sunLightRanges) {
    const s = Number(r[0])
    const e = Number(r[1])
    if (!Number.isFinite(s) || !Number.isFinite(e)) continue
    if (s > cursor) out.push([cursor, s])
    if (e > cursor) cursor = e
  }
  if (cursor < 17) out.push([cursor, 17])
  return out
}

/**
 * @param {object} props
 * @param {number} props.insolationAngleDeg
 * @param {number} [props.azimuthDeg]               defaults to 90 (north up)
 * @param {[number, number][]} [props.sunLightRanges]  highlighted yellow segments
 */
export function SunPathOverlay({ insolationAngleDeg, azimuthDeg, sunLightRanges = [], onAngleChange }) {
  const svgRef = useRef(null)
  const dragging = useRef(false)

  const azimuth = Number.isFinite(Number(azimuthDeg)) ? Number(azimuthDeg) : 90
  // Rotate the arc so cardinal directions align with the plan.
  // SVG `rotate()` is CW positive; cartesian azimuth is CCW positive → negate.
  const svgRotationDeg = -(azimuth - 90)

  function _applyDrag(e) {
    const svg = svgRef.current
    if (!svg || !onAngleChange) return
    // Convert screen coords → SVG viewBox coords (handles preserveAspectRatio letterboxing)
    const pt = svg.createSVGPoint()
    pt.x = e.clientX
    pt.y = e.clientY
    const ctm = svg.getScreenCTM()
    if (!ctm) return
    const svgPt = pt.matrixTransform(ctm.inverse())
    onAngleChange(_angleFromSvgPoint(svgPt.x, svgPt.y, svgRotationDeg))
  }

  function handlePointerDown(e) {
    e.currentTarget.setPointerCapture(e.pointerId)
    dragging.current = true
    _applyDrag(e)
  }

  function handlePointerMove(e) {
    if (!dragging.current) return
    _applyDrag(e)
  }

  function handlePointerUp(e) {
    dragging.current = false
    e.currentTarget.releasePointerCapture(e.pointerId)
  }

  const sunTheta = _angleToTheta(insolationAngleDeg)
  const sun = _polar(sunTheta)

  // Shoulder clip: reveal (sunrise) or hide (sunset) the sun icon near the arc endpoints.
  // SHOULDER_LEFT  spans theta [0, 15]  (06:00–07:00, east/sunrise).
  // SHOULDER_RIGHT spans theta [165,180] (17:00–18:00, west/sunset).
  let sunClipType = null
  let sunClipT = 1
  if (sunTheta >= 0 && sunTheta <= 15) {
    sunClipType = 'rise'
    sunClipT = sunTheta / 15
  } else if (sunTheta >= 165 && sunTheta <= 180) {
    sunClipType = 'set'
    sunClipT = (sunTheta - 165) / 15
  }

  const validRanges = sunLightRanges.filter(
    (r) => Array.isArray(r) && r.length === 2
      && Number.isFinite(Number(r[0])) && Number.isFinite(Number(r[1])),
  )
  const uncovered = _uncoveredSegments(validRanges)

  const SHOULDER_LEFT  = [_hourToTheta(6),  _hourToTheta(7)]
  const SHOULDER_RIGHT = [_hourToTheta(17), _hourToTheta(18)]

  return (
    <div className={styles.overlay} aria-hidden="true">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW} ${VIEW}`}
        preserveAspectRatio="xMidYMid meet"
        className={styles.svg}
      >
        <g transform={`rotate(${svgRotationDeg} ${CX} ${CY})`}>
          {/* dashed shoulders */}
          <path
            d={_arcPath(SHOULDER_LEFT[0],  SHOULDER_LEFT[1])}
            fill="none" stroke="#94a3b8" strokeWidth="1" strokeDasharray="4,3"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d={_arcPath(SHOULDER_RIGHT[0], SHOULDER_RIGHT[1])}
            fill="none" stroke="#94a3b8" strokeWidth="1" strokeDasharray="4,3"
            vectorEffect="non-scaling-stroke"
          />

          {/* uncovered (no-sun) ranges in grey */}
          {uncovered.map(([h1, h2], i) => (
            <path
              key={`u-${i}`}
              d={_arcPath(_hourToTheta(h1), _hourToTheta(h2))}
              fill="none" stroke="#94a3b8" strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {/* active sun-light ranges in yellow */}
          {validRanges.map(([h1, h2], i) => (
            <path
              key={`s-${i}`}
              d={_arcPath(_hourToTheta(Number(h1)), _hourToTheta(Number(h2)))}
              fill="none" stroke="#ffd000" strokeWidth="3.5"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          <SunIcon x={sun.x} y={sun.y} size={ICON_SIZE} clipType={sunClipType} clipT={sunClipT}
          // counterRotateDeg={-svgRotationDeg}
          />
          {onAngleChange && (
            <circle
              cx={sun.x} cy={sun.y} r={HIT_R}
              fill="transparent"
              className={`${styles.sunDragHit} bim-sun-drag`}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
          )}
        </g>
      </svg>
    </div>
  )
}
